import React from 'react';
import { Button, Input, Spinner } from 'reactstrap';
import { FiArrowLeft, FiEdit2, FiEye, FiPlus, FiTrash2, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { getLessons } from '../../../../Api/lms/apiUtils';
import {
  BankOption,
  BankQuestion,
  BankQuestionLocation,
  QuestionBankFilters,
  SaveQuestionPayload,
  createBankQuestion,
  deleteBankQuestion,
  getBankMediaUploadUrl,
  getBankQuestions,
  uploadBankMedia,
  updateBankQuestion,
} from './questionBankApi';
import './question-bank.scss';

type Props = { courses: any[] };
type EditorDraft = {
  question: string;
  questionFile: string;
  answerType: 'single' | 'multiple';
  answerExplanation: string;
  options: BankOption[];
  correctOptionNumbers: number[];
};
type QuestionAssignment = {
  key: string;
  courseId: string;
  levelId: string;
  lessonNumber: string;
  questionId?: string;
};
type RemovedQuestionMapping = QuestionAssignment & { questionId: string };
type DeleteBlockedLesson = { id: string; label: string; reason: string };
const createQuestionGroupId = () => `qbg-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const questionCountKey = (row: Pick<QuestionAssignment, 'courseId' | 'levelId' | 'lessonNumber'>) =>
  `${row.courseId}:${row.levelId}:${row.lessonNumber}`;

const emptyDraft = (): EditorDraft => ({
  question: '',
  questionFile: 'none',
  answerType: 'single',
  answerExplanation: '',
  options: [1, 2, 3, 4].map((optionNumber) => ({ optionNumber, optionType: 'text', value: '' })),
  correctOptionNumbers: [],
});
const emptyAssignment = (): QuestionAssignment => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  courseId: '',
  levelId: '',
  lessonNumber: '',
});

const idOfCourse = (course: any) => course?.course_id ?? course?.id;
const idOfLevel = (level: any) => level?.level_id ?? level?.id;
const nameOfCourse = (course: any) => course?.course_name ?? course?.name ?? '';
const nameOfLevel = (level: any) => level?.level_name ?? level?.name ?? '';
const lessonNumberOf = (lesson: any) => lesson?.lesson_number ?? lesson?.lessonNumber ?? lesson?.lesson_no;
const formatQuestionType = (questionType?: string) => {
  const readableTypes: Record<string, string> = {
    text: 'Text',
    text_image: 'Text and Image',
    text_audio: 'Text and Audio',
    text_video: 'Text and Video',
  };
  return readableTypes[String(questionType ?? '').toLowerCase()] || questionType || 'Text';
};
const mediaTypeFromUrl = (value?: string) => {
  if (!value || value === 'none') return '';
  const path = value.trim().split(/[?#]/, 1)[0].toLowerCase();
  const extension = path.slice(path.lastIndexOf('.') + 1);
  if (['jpg', 'jpeg', 'png'].includes(extension)) return 'image';
  if (['mp4', 'avi'].includes(extension)) return 'video';
  if (['mp3', 'wav'].includes(extension)) return 'audio';
  return '';
};
const isSupportedImageFile = (file: File) => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  const supportedExtensions = ['jpg', 'jpeg', 'png'];
  const supportedMimeTypes = ['image/jpeg', 'image/png'];
  return supportedExtensions.includes(extension ?? '')
    && (!file.type || supportedMimeTypes.includes(file.type.toLowerCase()));
};
const imageOnlyUploadError = 'Only JPG, JPEG, and PNG images are allowed.';
const levelOrder = (level: any) => {
  const name = String(level?.level_name ?? level?.name ?? '').toLowerCase();
  if (name.includes('intermediate')) return 0;
  if (name.includes('proficient')) return 1;
  if (name.includes('advanced')) return 2;
  return -1;
};
const assessmentTagNames = (lesson: any) => {
  const rawTags = lesson?.tags;
  const tags: any[] = Array.isArray(rawTags)
    ? rawTags
    : typeof rawTags === 'string'
      ? rawTags.split(',')
      : [];
  return tags.map((tag: any) => String(tag?.name ?? tag?.label ?? tag?.value ?? tag ?? '').toLowerCase());
};
const hasAssessmentTag = (lesson: any) => assessmentTagNames(lesson).some(
  (name) => name.includes('formative assessment') || name.includes('summative assessment')
);
const minimumQuestionCountForLesson = (lesson: any) => {
  const names = assessmentTagNames(lesson);
  if (names.some((name) => name.includes('summative assessment'))) return 15;
  if (names.some((name) => name.includes('formative assessment'))) return 10;
  return 0;
};

const messageFromError = (error: any) =>
  error?.response?.data?.message || error?.message || 'Something went wrong. Please try again.';

const asDraft = (question: BankQuestion): EditorDraft => {
  const answerType = question.answerType === 'multiple' ? 'multiple' : 'single';
  const options = (question.options ?? []).map((option) => ({ ...option }));
  const matchingOptions = options
    .filter((option) => (question.answer ?? []).includes(option.value))
    .sort((left, right) => left.optionNumber - right.optionNumber);

  // Answers are stored as option text, so duplicate option values cannot tell
  // us which option was intended. Leave it unselected for the editor to resolve.
  const correctOptionNumbers = answerType === 'single'
    ? matchingOptions.length === 1 ? [matchingOptions[0].optionNumber] : []
    : matchingOptions.map((option) => option.optionNumber);

  return {
    question: question.question ?? '',
    questionFile: question.questionFile && question.questionFile !== 'none' ? question.questionFile : '',
    answerType,
    answerExplanation: question.answerExplanation ?? '',
    options,
    correctOptionNumbers,
  };
};

const QuestionBankEditor: React.FC<Props> = ({ courses }) => {
  const [courseId, setCourseId] = React.useState('');
  const [levelId, setLevelId] = React.useState('');
  const [lessonNumber, setLessonNumber] = React.useState('');
  const [lessons, setLessons] = React.useState<any[]>([]);
  const [questions, setQuestions] = React.useState<BankQuestion[]>([]);
  const [loadingLessons, setLoadingLessons] = React.useState(false);
  const [loadingQuestions, setLoadingQuestions] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [questionMediaUploading, setQuestionMediaUploading] = React.useState(false);
  const [uploadingOptionNumber, setUploadingOptionNumber] = React.useState<number | null>(null);
  const [copiedMediaUrl, setCopiedMediaUrl] = React.useState('');
  const [deleting, setDeleting] = React.useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [checkingDeleteEligibility, setCheckingDeleteEligibility] = React.useState(false);
  const [deleteBlockedLessons, setDeleteBlockedLessons] = React.useState<DeleteBlockedLesson[]>([]);
  const [deleteCheckError, setDeleteCheckError] = React.useState('');
  const [editingQuestion, setEditingQuestion] = React.useState<BankQuestion | null>(null);
  const [isAdding, setIsAdding] = React.useState(false);
  const [draft, setDraft] = React.useState<EditorDraft>(emptyDraft);
  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);
  const [previewSelection, setPreviewSelection] = React.useState<number[]>([]);
  const [previewSubmitted, setPreviewSubmitted] = React.useState(false);
  const [assignmentRows, setAssignmentRows] = React.useState<QuestionAssignment[]>([]);
  const [removedMappings, setRemovedMappings] = React.useState<RemovedQuestionMapping[]>([]);
  const [questionCounts, setQuestionCounts] = React.useState<Record<string, number>>({});
  const [loadingQuestionCounts, setLoadingQuestionCounts] = React.useState<Record<string, boolean>>({});
  const [questionGroupId, setQuestionGroupId] = React.useState('');
  const [assignmentLessons, setAssignmentLessons] = React.useState<Record<string, any[]>>({});
  const [loadingAssignmentLevels, setLoadingAssignmentLevels] = React.useState<Record<string, boolean>>({});

  const levelsForCourse = (courseIdValue: string) => {
    const course = courses.find((item) => String(idOfCourse(item)) === courseIdValue);
    return (course?.levels ?? [])
    .filter((level: any) => {
      const name = String(level?.level_name ?? level?.name ?? '').toLowerCase();
      return !name.includes('demo') && !name.includes('preparatory') && !/^prep(?:$|\s)/.test(name) && levelOrder(level) >= 0;
    })
    .sort((left: any, right: any) => levelOrder(left) - levelOrder(right));
  };
  const levels = levelsForCourse(courseId);
  const assessmentLessons = lessons.filter(hasAssessmentTag);
  const filtersReady = Boolean(courseId && levelId && lessonNumber);
  const currentFilters: QuestionBankFilters | null = filtersReady
    ? { courseId: Number(courseId), levelId: Number(levelId), lessonNumber }
    : null;

  const questionRowRemovalReason = (row: QuestionAssignment) => {
    if (!row.questionId) return '';
    if (loadingAssignmentLevels[row.levelId] || !assignmentLessons[row.levelId]) return 'Loading this FA/SA lesson’s details.';
    const rowLesson = (assignmentLessons[row.levelId] ?? []).find(
      (lesson) => String(lessonNumberOf(lesson)) === row.lessonNumber
    );
    if (!rowLesson || !hasAssessmentTag(rowLesson)) return 'Could not verify this FA/SA lesson.';
    const key = questionCountKey(row);
    if (loadingQuestionCounts[key] || questionCounts[key] === undefined) return 'Checking this lesson’s question count.';
    const minimum = minimumQuestionCountForLesson(rowLesson);
    const alreadyMarkedForRemoval = removedMappings.filter((mapping) => questionCountKey(mapping) === key).length;
    if (minimum && questionCounts[key] - alreadyMarkedForRemoval - 1 < minimum) {
      return `This ${minimum === 15 ? 'summative' : 'formative'} assessment lesson must keep at least ${minimum} questions.`;
    }
    return '';
  };

  const labelForQuestionLocation = (location: BankQuestionLocation) => {
    const course = courses.find((item) => String(idOfCourse(item)) === String(location.courseId));
    const courseName = location.courseName || nameOfCourse(course) || 'Course';
    const courseLevel = location.courseLevel || nameOfLevel(
      levelsForCourse(String(location.courseId)).find((level: any) => String(idOfLevel(level)) === String(location.levelId))
    ) || 'Level';
    return `${courseName} · ${courseLevel} · Lesson ${location.lessonNumber}`;
  };

  const requestMediaUpload = async (file: File, mediaRole: 'question' | `option-${number}`) => {
    const primaryAssignment = assignmentRows[0];
    const assignmentCourse = courses.find((course) => String(idOfCourse(course)) === primaryAssignment?.courseId);
    const assignmentLevel = levelsForCourse(primaryAssignment?.courseId ?? '').find((level: any) => String(idOfLevel(level)) === primaryAssignment?.levelId);
    if (!assignmentCourse || !assignmentLevel || !primaryAssignment?.lessonNumber) {
      throw new Error('Select a course, level, and FA/SA lesson before uploading media.');
    }
    const upload = await getBankMediaUploadUrl({
      fileName: file.name,
      courseName: nameOfCourse(assignmentCourse),
      courseLevel: nameOfLevel(assignmentLevel),
      lessonNumber: primaryAssignment.lessonNumber,
      mediaRole,
    });
    await uploadBankMedia(upload.uploadUrl, file, upload.contentType);
    return upload.cloudFrontUrl;
  };

  const handleQuestionMediaChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!file) return;
    if (!isSupportedImageFile(file)) {
      toast.error(imageOnlyUploadError);
      return;
    }
    setQuestionMediaUploading(true);
    try {
      const cloudFrontUrl = await requestMediaUpload(file, 'question');
      setDraft((previous) => ({ ...previous, questionFile: cloudFrontUrl }));
      toast.success('Question media uploaded.');
    } catch (error) {
      toast.error(messageFromError(error));
    } finally {
      setQuestionMediaUploading(false);
    }
  };

  const handleOptionMediaChange = async (optionNumber: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!file) return;
    if (!isSupportedImageFile(file)) {
      toast.error(imageOnlyUploadError);
      return;
    }
    setUploadingOptionNumber(optionNumber);
    try {
      const cloudFrontUrl = await requestMediaUpload(file, `option-${optionNumber}`);
      const optionType = mediaTypeFromUrl(cloudFrontUrl);
      setDraft((previous) => ({
        ...previous,
        options: previous.options.map((option) => option.optionNumber === optionNumber
          ? { ...option, value: cloudFrontUrl, optionFile: cloudFrontUrl, optionType }
          : option),
      }));
      toast.success('Option media uploaded.');
    } catch (error) {
      toast.error(messageFromError(error));
    } finally {
      setUploadingOptionNumber(null);
    }
  };

  const renderMediaPreview = (url: string, label: string) => {
    const mediaType = mediaTypeFromUrl(url);
    if (!mediaType) return null;
    if (mediaType === 'image') {
      return <img className="question-bank__media-preview" src={url} alt={label} />;
    }
    if (mediaType === 'video') {
      return <video className="question-bank__media-preview" src={url} controls preload="metadata" aria-label={label} />;
    }
    return <audio className="question-bank__audio-preview" src={url} controls preload="metadata" aria-label={label} />;
  };

  const copyMediaUrl = async (url: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const temporaryInput = document.createElement('textarea');
        temporaryInput.value = url;
        temporaryInput.style.position = 'fixed';
        temporaryInput.style.opacity = '0';
        document.body.appendChild(temporaryInput);
        temporaryInput.select();
        const copied = document.execCommand('copy');
        document.body.removeChild(temporaryInput);
        if (!copied) throw new Error('Clipboard access is unavailable.');
      }
      setCopiedMediaUrl(url);
      window.setTimeout(() => setCopiedMediaUrl(''), 1800);
    } catch {
      toast.error('Could not copy the media link.');
    }
  };

  const renderMediaLinkControls = (url: string, label: string) => {
    const mediaUrl = url && url !== 'none' ? url : '';
    return (
      <div className="question-bank__media-link-row">
        <Input readOnly value={mediaUrl} placeholder="Upload media to generate a link" aria-label={`${label} URL`} />
        {mediaUrl && (
          <>
            <Button type="button" color="light" onClick={() => copyMediaUrl(mediaUrl)}>
              {copiedMediaUrl === mediaUrl ? 'Copied' : 'Copy link'}
            </Button>
            {/^https?:\/\//i.test(mediaUrl) && (
              <a className="btn btn-light" href={mediaUrl} target="_blank" rel="noopener noreferrer">Open</a>
            )}
          </>
        )}
      </div>
    );
  };

  React.useEffect(() => {
    setLevelId('');
    setLessonNumber('');
    setLessons([]);
    setQuestions([]);
    setEditingQuestion(null);
    setIsAdding(false);
  }, [courseId]);

  React.useEffect(() => {
    setLessonNumber('');
    setLessons([]);
    setQuestions([]);
    setEditingQuestion(null);
    setIsAdding(false);
    if (!levelId) return;
    let cancelled = false;
    setLoadingLessons(true);
    getLessons(levelId)
      .then((response) => {
        if (!cancelled) setLessons(Array.isArray(response?.lessons) ? response.lessons : []);
      })
      .catch((error) => {
        if (!cancelled) toast.error(messageFromError(error));
      })
      .finally(() => {
        if (!cancelled) setLoadingLessons(false);
      });
    return () => { cancelled = true; };
  }, [levelId]);

  React.useEffect(() => {
    setQuestions([]);
    setEditingQuestion(null);
    setIsAdding(false);
    if (!currentFilters) return;
    let cancelled = false;
    setLoadingQuestions(true);
    getBankQuestions(currentFilters)
      .then((result) => {
        if (!cancelled) {
          setQuestions(result);
          setQuestionCounts((previous) => ({ ...previous, [questionCountKey({
            courseId: String(currentFilters.courseId),
            levelId: String(currentFilters.levelId),
            lessonNumber: currentFilters.lessonNumber,
          })]: result.length }));
        }
      })
      .catch((error) => {
        if (!cancelled) toast.error(messageFromError(error));
      })
      .finally(() => {
        if (!cancelled) setLoadingQuestions(false);
      });
    return () => { cancelled = true; };
  }, [courseId, levelId, lessonNumber]);

  const loadAssignmentLessons = async (assignmentLevelId: string) => {
    if (!assignmentLevelId || assignmentLessons[assignmentLevelId]) return;
    setLoadingAssignmentLevels((previous) => ({ ...previous, [assignmentLevelId]: true }));
    try {
      const response = await getLessons(assignmentLevelId);
      setAssignmentLessons((previous) => ({
        ...previous,
        [assignmentLevelId]: Array.isArray(response?.lessons) ? response.lessons : [],
      }));
    } catch (error) {
      toast.error(messageFromError(error));
    } finally {
      setLoadingAssignmentLevels((previous) => ({ ...previous, [assignmentLevelId]: false }));
    }
  };

  const loadAssignmentQuestionCount = async (row: QuestionAssignment) => {
    const key = questionCountKey(row);
    if (questionCounts[key] !== undefined || loadingQuestionCounts[key]) return;
    if (String(currentFilters?.courseId) === row.courseId
      && String(currentFilters?.levelId) === row.levelId
      && currentFilters?.lessonNumber === row.lessonNumber) {
      setQuestionCounts((previous) => ({ ...previous, [key]: questions.length }));
      return;
    }
    setLoadingQuestionCounts((previous) => ({ ...previous, [key]: true }));
    try {
      const result = await getBankQuestions({
        courseId: Number(row.courseId),
        levelId: Number(row.levelId),
        lessonNumber: row.lessonNumber,
      });
      setQuestionCounts((previous) => ({ ...previous, [key]: result.length }));
    } catch (error) {
      toast.error(messageFromError(error));
    } finally {
      setLoadingQuestionCounts((previous) => ({ ...previous, [key]: false }));
    }
  };

  const beginAdd = () => {
    setEditingQuestion(null);
    setDraft(emptyDraft());
    setAssignmentRows([emptyAssignment()]);
    setRemovedMappings([]);
    setQuestionGroupId(createQuestionGroupId());
    setIsAdding(true);
    setIsPreviewOpen(false);
  };

  const beginEdit = (question: BankQuestion) => {
    const locations: BankQuestionLocation[] = question.locations?.length
      ? question.locations
      : [{
        id: question.id,
        courseId: question.courseId,
        courseName: question.courseName,
        levelId: question.levelId,
        courseLevel: question.courseLevel,
        lessonNumber: question.lessonNumber,
      }];
    const orderedLocations = locations.slice().sort((left, right) =>
      left.id === question.id ? -1 : right.id === question.id ? 1 : 0
    );
    const groupId = question.questionGroupId || createQuestionGroupId();
    setEditingQuestion({ ...question, questionGroupId: groupId, locations: orderedLocations });
    setDraft(asDraft(question));
    setAssignmentRows(orderedLocations.map((location) => ({
      key: `${question.id}-${location.id}`,
      questionId: location.id,
      courseId: String(location.courseId),
      levelId: String(location.levelId),
      lessonNumber: String(location.lessonNumber),
    })));
    setRemovedMappings([]);
    setQuestionGroupId(groupId);
    orderedLocations.forEach((location) => {
      const row = {
        key: `${question.id}-${location.id}`,
        questionId: location.id,
        courseId: String(location.courseId),
        levelId: String(location.levelId),
        lessonNumber: String(location.lessonNumber),
      };
      loadAssignmentLessons(row.levelId);
      loadAssignmentQuestionCount(row);
    });
    setIsAdding(false);
    setIsPreviewOpen(false);
  };

  const closeEditor = () => {
    setEditingQuestion(null);
    setIsAdding(false);
    setAssignmentRows([]);
    setRemovedMappings([]);
    setQuestionGroupId('');
    setIsPreviewOpen(false);
    setIsDeleteDialogOpen(false);
  };

  const addAssignmentRow = () => setAssignmentRows((previous) => [...previous, emptyAssignment()]);

  const removeAssignmentRow = (key: string) => {
    const row = assignmentRows.find((assignment) => assignment.key === key);
    if (assignmentRows.length <= 1 || !row || questionRowRemovalReason(row)) return;
    if (row.questionId) {
      const questionId = row.questionId;
      setRemovedMappings((previous) => previous.some((mapping) => mapping.questionId === questionId)
        ? previous
        : [...previous, { ...row, questionId }]);
    }
    setAssignmentRows((previous) => previous.filter((assignment) => assignment.key !== key));
  };

  const updateAssignmentRow = (key: string, changes: Partial<QuestionAssignment>) => {
    setAssignmentRows((previous) => previous.map((row) => row.key === key ? { ...row, ...changes } : row));
  };

  const handleAssignmentLevelChange = (row: QuestionAssignment, value: string) => {
    updateAssignmentRow(row.key, { levelId: value, lessonNumber: '' });
    if (value) loadAssignmentLessons(value);
  };

  const openPreview = () => {
    setPreviewSelection([]);
    setPreviewSubmitted(false);
    setIsPreviewOpen(true);
  };

  const togglePreviewOption = (optionNumber: number) => {
    if (previewSubmitted) return;
    setPreviewSelection((previous) => {
      if (draft.answerType === 'single') return [optionNumber];
      return previous.includes(optionNumber)
        ? previous.filter((number) => number !== optionNumber)
        : [...previous, optionNumber];
    });
  };

  const previewIsCorrect = previewSubmitted
    && previewSelection.length === draft.correctOptionNumbers.length
    && previewSelection.every((number) => draft.correctOptionNumbers.includes(number));

  const setOption = (optionNumber: number, changes: Partial<BankOption>) => {
    setDraft((previous) => ({
      ...previous,
      options: previous.options.map((option) =>
        option.optionNumber === optionNumber ? { ...option, ...changes } : option
      ),
    }));
  };

  const toggleAnswer = (optionNumber: number) => {
    setDraft((previous) => {
      const alreadySelected = previous.correctOptionNumbers.includes(optionNumber);
      const correctOptionNumbers = previous.answerType === 'single'
        ? (alreadySelected ? [] : [optionNumber])
        : alreadySelected
          ? previous.correctOptionNumbers.filter((number) => number !== optionNumber)
          : [...previous.correctOptionNumbers, optionNumber];
      return { ...previous, correctOptionNumbers };
    });
  };

  const addOption = () => {
    if (draft.options.length >= 6) return;
    const usedNumbers = new Set(draft.options.map((option) => option.optionNumber));
    const optionNumber = [1, 2, 3, 4, 5, 6].find((number) => !usedNumbers.has(number));
    if (optionNumber === undefined) return;
    setDraft((previous) => ({
      ...previous,
      options: [...previous.options, { optionNumber, optionType: 'text', value: '' }]
        .sort((left, right) => left.optionNumber - right.optionNumber),
    }));
  };

  const removeOption = (optionNumber: number) => {
    if (draft.options.length <= 3) return;
    setDraft((previous) => ({
      ...previous,
      options: previous.options.filter((option) => option.optionNumber !== optionNumber),
      correctOptionNumbers: previous.correctOptionNumbers.filter((number) => number !== optionNumber),
    }));
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!assignmentRows.length) {
      toast.error('Add at least one FA/SA lesson.');
      return;
    }
    if (assignmentRows.some((row) => !row.courseId || !row.levelId || !row.lessonNumber)) {
      toast.error('Complete the course, level, and lesson for every FA/SA lesson.');
      return;
    }
    const assignmentKeys = assignmentRows.map((row) => `${row.courseId}:${row.levelId}:${row.lessonNumber}`);
    if (new Set(assignmentKeys).size !== assignmentKeys.length) {
      toast.error('The same course, level, and lesson cannot be added more than once.');
      return;
    }
    const invalidAssessmentAssignment = assignmentRows.find((row) => {
      const targetLessons = assignmentLessons[row.levelId];
      const targetLesson = targetLessons?.find((lesson) => String(lessonNumberOf(lesson)) === row.lessonNumber);
      return !targetLesson || !hasAssessmentTag(targetLesson);
    });
    if (invalidAssessmentAssignment) {
      toast.error('Each selected lesson must have a formative or summative assessment.');
      return;
    }
    if (!draft.question.trim()) {
      toast.error('Enter the question text.');
      return;
    }
    if (draft.options.length < 3 || draft.options.some((option) => !option.value.trim())) {
      toast.error('Provide at least 3 options and fill in each option.');
      return;
    }
    if (!draft.correctOptionNumbers.length) {
      toast.error('Select the correct answer.');
      return;
    }
    if (draft.answerType === 'single' && draft.correctOptionNumbers.length !== 1) {
      toast.error('A single-answer question must have exactly one correct option.');
      return;
    }

    if (removedMappings.length) {
      const removalsByLocation = new Map<string, { row: RemovedQuestionMapping; count: number }>();
      removedMappings.forEach((row) => {
        const key = questionCountKey(row);
        const existing = removalsByLocation.get(key);
        removalsByLocation.set(key, { row, count: (existing?.count ?? 0) + 1 });
      });
      try {
        const freshCounts = await Promise.all(Array.from(removalsByLocation.entries()).map(async ([key, removal]) => {
          const lesson = (assignmentLessons[removal.row.levelId] ?? []).find(
            (item) => String(lessonNumberOf(item)) === removal.row.lessonNumber
          );
          if (!lesson || !hasAssessmentTag(lesson)) {
            throw new Error('Could not verify one of the FA/SA lessons being removed.');
          }
          const currentQuestions = await getBankQuestions({
            courseId: Number(removal.row.courseId),
            levelId: Number(removal.row.levelId),
            lessonNumber: removal.row.lessonNumber,
          });
          const minimum = minimumQuestionCountForLesson(lesson);
          if (minimum && currentQuestions.length - removal.count < minimum) {
            throw new Error(`This ${minimum === 15 ? 'summative' : 'formative'} assessment lesson must keep at least ${minimum} questions.`);
          }
          return [key, currentQuestions.length] as const;
        }));
        setQuestionCounts((previous) => ({ ...previous, ...Object.fromEntries(freshCounts) }));
      } catch (error) {
        toast.error(messageFromError(error));
        return;
      }
    }

    setSaving(true);
    let savedAssignments = 0;
    try {
      for (let index = 0; index < assignmentRows.length; index += 1) {
        const row = assignmentRows[index];
        const assignmentCourse = courses.find((course) => String(idOfCourse(course)) === row.courseId);
        const assignmentLevel = levelsForCourse(row.courseId).find((level: any) => String(idOfLevel(level)) === row.levelId);
        if (!assignmentCourse || !assignmentLevel) throw new Error('A selected course or level is no longer available.');
        const payload: SaveQuestionPayload = {
          question: draft.question.trim(),
          questionFile: draft.questionFile.trim() || 'none',
          answerType: draft.answerType,
          answerExplanation: draft.answerExplanation.trim(),
          questionGroupId,
          options: draft.options.map((option) => ({ ...option, value: option.value.trim() })),
          correctOptionNumbers: draft.correctOptionNumbers,
          courseId: Number(row.courseId),
          levelId: Number(row.levelId),
          lessonNumber: row.lessonNumber,
          courseName: nameOfCourse(assignmentCourse),
          courseLevel: nameOfLevel(assignmentLevel),
        };
        if (row.questionId) {
          await updateBankQuestion(row.questionId, payload);
        } else {
          const createdQuestion = await createBankQuestion(payload);
          if (createdQuestion?.id) {
            setAssignmentRows((previous) => previous.map((assignment) => assignment.key === row.key
              ? { ...assignment, questionId: createdQuestion.id }
              : assignment));
            const key = questionCountKey(row);
            setQuestionCounts((previous) => previous[key] === undefined
              ? previous
              : { ...previous, [key]: previous[key] + 1 });
          }
        }
        savedAssignments += 1;
      }
      for (const mapping of removedMappings) {
        await deleteBankQuestion(mapping.questionId);
        const key = questionCountKey(mapping);
        setQuestionCounts((previous) => previous[key] === undefined
          ? previous
          : { ...previous, [key]: Math.max(0, previous[key] - 1) });
        setRemovedMappings((previous) => previous.filter((removed) => removed.questionId !== mapping.questionId));
      }
      toast.success(`${editingQuestion ? 'Question updated' : 'Question added'} for ${savedAssignments} FA/SA lesson${savedAssignments === 1 ? '' : 's'}.`);
      setEditingQuestion(null);
      setIsAdding(false);
      setAssignmentRows([]);
      setRemovedMappings([]);
      if (currentFilters) setQuestions(await getBankQuestions(currentFilters));
    } catch (error) {
      const message = savedAssignments
        ? `Saved ${savedAssignments} of ${assignmentRows.length} FA/SA lessons before an error. ${messageFromError(error)}`
        : messageFromError(error);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = async () => {
    if (!editingQuestion) return;
    const locations: BankQuestionLocation[] = editingQuestion.locations?.length
      ? editingQuestion.locations
      : [{
        id: editingQuestion.id,
        courseId: editingQuestion.courseId,
        courseName: editingQuestion.courseName,
        levelId: editingQuestion.levelId,
        courseLevel: editingQuestion.courseLevel,
        lessonNumber: editingQuestion.lessonNumber,
      }];
    setDeleteBlockedLessons([]);
    setDeleteCheckError('');
    setIsDeleteDialogOpen(true);
    setCheckingDeleteEligibility(true);
    const fetchedLessons = new Map<string, any[]>();
    setDeleting(true);
    try {
      const results = await Promise.all(locations.map(async (location) => {
        const levelKey = String(location.levelId);
        let levelLessons = assignmentLessons[levelKey] ?? fetchedLessons.get(levelKey);
        if (!levelLessons) {
          const response = await getLessons(levelKey);
          levelLessons = Array.isArray(response?.lessons) ? response.lessons : [];
          fetchedLessons.set(levelKey, levelLessons);
        }
        const lesson = levelLessons.find((item) => String(lessonNumberOf(item)) === String(location.lessonNumber));
        if (!lesson || !hasAssessmentTag(lesson)) {
          return {
            id: location.id,
            label: labelForQuestionLocation(location),
            reason: 'Could not verify this FA/SA lesson.',
          };
        }
        const lessonQuestions = await getBankQuestions({
          courseId: Number(location.courseId),
          levelId: Number(location.levelId),
          lessonNumber: String(location.lessonNumber),
        });
        const minimum = minimumQuestionCountForLesson(lesson);
        const matchingEntriesToDelete = locations.filter((candidate) =>
          String(candidate.courseId) === String(location.courseId)
          && String(candidate.levelId) === String(location.levelId)
          && String(candidate.lessonNumber) === String(location.lessonNumber)
        ).length;
        setQuestionCounts((previous) => ({
          ...previous,
          [questionCountKey({
            courseId: String(location.courseId),
            levelId: String(location.levelId),
            lessonNumber: String(location.lessonNumber),
          })]: lessonQuestions.length,
        }));
        return minimum && lessonQuestions.length - matchingEntriesToDelete < minimum
          ? {
            id: location.id,
            label: labelForQuestionLocation(location),
            reason: `Would fall below the minimum of ${minimum} questions.`,
          }
          : null;
      }));
      if (fetchedLessons.size) {
        setAssignmentLessons((previous) => ({ ...previous, ...Object.fromEntries(fetchedLessons) }));
      }
      setDeleteBlockedLessons(results.filter((result): result is DeleteBlockedLesson => Boolean(result)));
    } catch (error) {
      setDeleteCheckError(`Could not verify every linked lesson. ${messageFromError(error)}`);
    } finally {
      setDeleting(false);
      setCheckingDeleteEligibility(false);
    }
  };

  const confirmDeleteAll = async () => {
    if (!editingQuestion || checkingDeleteEligibility || deleteBlockedLessons.length || deleteCheckError) return;
    const locations: BankQuestionLocation[] = editingQuestion.locations?.length
      ? editingQuestion.locations
      : [{
        id: editingQuestion.id,
        courseId: editingQuestion.courseId,
        courseName: editingQuestion.courseName,
        levelId: editingQuestion.levelId,
        courseLevel: editingQuestion.courseLevel,
        lessonNumber: editingQuestion.lessonNumber,
      }];
    setDeleting(true);
    const deletedIds: string[] = [];
    try {
      for (const location of locations) {
        await deleteBankQuestion(location.id);
        deletedIds.push(location.id);
        const key = questionCountKey({
          courseId: String(location.courseId),
          levelId: String(location.levelId),
          lessonNumber: String(location.lessonNumber),
        });
        setQuestionCounts((previous) => previous[key] === undefined
          ? previous
          : { ...previous, [key]: Math.max(0, previous[key] - 1) });
        setAssignmentRows((previous) => previous.filter((row) => row.questionId !== location.id));
        setRemovedMappings((previous) => previous.filter((row) => row.questionId !== location.id));
        setEditingQuestion((previous) => previous ? {
          ...previous,
          locations: (previous.locations ?? []).filter((item) => item.id !== location.id),
        } : previous);
        setQuestions((previous) => previous.filter((question) => question.id !== location.id));
      }
      toast.success(`Question deleted from all ${locations.length} linked FA/SA lesson${locations.length === 1 ? '' : 's'}.`);
      closeEditor();
      if (currentFilters) setQuestions(await getBankQuestions(currentFilters));
    } catch (error) {
      setIsDeleteDialogOpen(false);
      toast.error(`Deleted from ${deletedIds.length} of ${locations.length} lessons. ${messageFromError(error)} Reopen Delete question to retry for the remaining lesson${locations.length - deletedIds.length === 1 ? '' : 's'}.`);
    } finally {
      setDeleting(false);
    }
  };

  const renderFilters = () => (
    <div className="question-bank__filters">
      <label>
        <span>Course</span>
        <Input type="select" value={courseId} onChange={(event) => setCourseId(event.target.value)}>
          <option value="">Select a course</option>
          {courses.map((course) => (
            <option key={idOfCourse(course)} value={idOfCourse(course)}>{nameOfCourse(course)}</option>
          ))}
        </Input>
      </label>
      <label>
        <span>Level</span>
        <Input type="select" value={levelId} disabled={!courseId} onChange={(event) => setLevelId(event.target.value)}>
          <option value="">Select a level</option>
          {levels.map((level: any) => (
            <option key={idOfLevel(level)} value={idOfLevel(level)}>{nameOfLevel(level)}</option>
          ))}
        </Input>
      </label>
      <label>
        <span>Lesson</span>
        <Input type="select" value={lessonNumber} disabled={!levelId || loadingLessons} onChange={(event) => setLessonNumber(event.target.value)}>
          <option value="">
            {loadingLessons
              ? 'Loading lessons…'
              : levelId && assessmentLessons.length === 0
                ? 'No formative/summative assessment lessons'
                : 'Select a lesson'}
          </option>
          {assessmentLessons.map((lesson) => {
            const number = lessonNumberOf(lesson);
            return number == null ? null : (
              <option key={`${number}-${lesson.lesson_id ?? ''}`} value={number}>
                Lesson {number}{lesson.lesson_name ? ` — ${lesson.lesson_name}` : ''}
              </option>
            );
          })}
        </Input>
      </label>
    </div>
  );

  const renderEditor = () => (
    <form className="question-bank__editor" onSubmit={handleSave}>
      <div className="question-bank__editor-top">
        <Button type="button" color="link" className="question-bank__back" onClick={closeEditor}>
          <FiArrowLeft /> Back to questions
        </Button>
        <span>{editingQuestion ? 'Edit question' : 'Add question'}</span>
        <Button type="button" color="light" className="question-bank__preview-trigger" onClick={openPreview}>
          <FiEye /> Preview as learner
        </Button>
      </div>

      <div className="question-bank__assignment-section">
        <div className="question-bank__assignment-heading">
          <div>
            <strong>FA/SA Lessons <b>*</b></strong>
            <p>Choose a course and level, then select the formative or summative assessment lesson.</p>
          </div>
          <Button type="button" color="light" onClick={addAssignmentRow}><FiPlus /> Add FA/SA lesson</Button>
        </div>
        {assignmentRows.map((row, index) => {
          const rowLevels = levelsForCourse(row.courseId);
          const rowLessons = (assignmentLessons[row.levelId] ?? []).filter(hasAssessmentTag);
          return (
            <div className="question-bank__assignment-row" key={row.key}>
              <div className="question-bank__assignment-row-title">
                <strong>FA/SA Lesson {index + 1}{editingQuestion && index === 0 ? ' · existing' : ''}</strong>
                {assignmentRows.length > 1 && (
                  <span className="question-bank__mapping-remove-wrap" title={questionRowRemovalReason(row) || undefined}>
                    <Button type="button" color="link" className="question-bank__assignment-remove" disabled={Boolean(questionRowRemovalReason(row))} onClick={() => removeAssignmentRow(row.key)}>
                      Remove
                    </Button>
                  </span>
                )}
              </div>
              <label>
                <span>Course</span>
                <Input type="select" value={row.courseId} onChange={(event) => {
                  updateAssignmentRow(row.key, { courseId: event.target.value, levelId: '', lessonNumber: '' });
                }}>
                  <option value="">Select a course</option>
                  {courses.map((course) => (
                    <option key={idOfCourse(course)} value={idOfCourse(course)}>{nameOfCourse(course)}</option>
                  ))}
                </Input>
              </label>
              <label>
                <span>Level</span>
                <Input type="select" value={row.levelId} disabled={!row.courseId} onChange={(event) => handleAssignmentLevelChange(row, event.target.value)}>
                  <option value="">Select a level</option>
                  {rowLevels.map((level: any) => (
                    <option key={idOfLevel(level)} value={idOfLevel(level)}>{nameOfLevel(level)}</option>
                  ))}
                </Input>
              </label>
              <label>
                <span>FA/SA lesson</span>
                <Input type="select" value={row.lessonNumber} disabled={!row.levelId || loadingAssignmentLevels[row.levelId]} onChange={(event) => updateAssignmentRow(row.key, { lessonNumber: event.target.value })}>
                  <option value="">
                    {loadingAssignmentLevels[row.levelId]
                      ? 'Loading lessons…'
                      : row.levelId && rowLessons.length === 0
                        ? 'No formative/summative assessment lessons'
                        : 'Select a lesson'}
                  </option>
                  {rowLessons.map((lesson) => {
                    const number = lessonNumberOf(lesson);
                    return number == null ? null : (
                      <option key={`${row.levelId}-${number}-${lesson.lesson_id ?? ''}`} value={number}>
                        Lesson {number}{lesson.lesson_name ? ` — ${lesson.lesson_name}` : ''}
                      </option>
                    );
                  })}
                </Input>
              </label>
            </div>
          );
        })}
        <small>Each FA/SA lesson is stored as its own entry. Remove a lesson row and save changes to remove the question from that lesson.</small>
      </div>

      <label className="question-bank__field">
        <span>Question <b>*</b></span>
        <Input type="textarea" rows={3} value={draft.question} onChange={(event) => setDraft({ ...draft, question: event.target.value })} required />
      </label>
      <div className="question-bank__field">
        <span>Question media link <small>(read-only)</small></span>
        <div className="question-bank__media-management">
          {renderMediaLinkControls(draft.questionFile, 'Question media')}
        </div>
        <div className="question-bank__upload-control">
          <label>Upload image
            <Input type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" disabled={questionMediaUploading} onChange={handleQuestionMediaChange} />
          </label>
          {questionMediaUploading && <span><Spinner size="sm" /> Uploading media…</span>}
        </div>
        {renderMediaPreview(draft.questionFile, 'Question media')}
        {draft.questionFile && draft.questionFile !== 'none' && (
          <Button
            type="button"
            color="light"
            className="question-bank__media-clear"
            aria-label="Delete Media"
            disabled={questionMediaUploading || saving}
            onClick={() => setDraft((previous) => ({ ...previous, questionFile: 'none' }))}
          >
            <FiTrash2 /> Delete Media
          </Button>
        )}
      </div>

      <div className="question-bank__form-row">
        <label className="question-bank__field">
          <span>Answer type</span>
          <Input type="select" value={draft.answerType} onChange={(event) => setDraft({ ...draft, answerType: event.target.value as 'single' | 'multiple', correctOptionNumbers: [] })}>
            <option value="single">Single answer</option>
            <option value="multiple">Multiple answers</option>
          </Input>
        </label>
        <div className="question-bank__answer-hint">Choose the correct answer{draft.answerType === 'multiple' ? 's' : ''} below.</div>
      </div>

      <div className="question-bank__options-heading">
        <div><strong>Options</strong><span> {draft.options.length} of 6 · at least 3 required</span></div>
        <Button type="button" color="link" disabled={draft.options.length >= 6} onClick={addOption}><FiPlus /> Add option</Button>
      </div>
      <div className="question-bank__options">
        {draft.options.map((option) => {
          const mediaUrl = option.optionFile || (mediaTypeFromUrl(option.value) ? option.value : '');
          const isMediaOption = Boolean(mediaUrl);
          return (
          <div className="question-bank__option-editor" key={option.optionNumber}>
            <div className="question-bank__option-row">
              <span className="question-bank__option-number">{String.fromCharCode(64 + option.optionNumber)}</span>
              {isMediaOption ? (
                <Input readOnly value={mediaUrl} placeholder={`Option ${option.optionNumber}`} aria-label={`Option ${option.optionNumber} media URL`} />
              ) : (
                <Input
                  value={option.value}
                  onChange={(event) => setOption(option.optionNumber, { value: event.target.value, optionFile: undefined, optionType: 'text' })}
                  placeholder={`Option ${option.optionNumber}`}
                  aria-label={`Option ${option.optionNumber}`}
                />
              )}
              <label className="question-bank__correct">
                <input name="correct-answer" type={draft.answerType === 'single' ? 'radio' : 'checkbox'} checked={draft.correctOptionNumbers.includes(option.optionNumber)} onChange={() => toggleAnswer(option.optionNumber)} />
                Correct
              </label>
              <Button type="button" color="link" className="question-bank__remove-option" disabled={draft.options.length <= 3} onClick={() => removeOption(option.optionNumber)} aria-label={`Remove option ${option.optionNumber}`}><FiTrash2 /></Button>
            </div>
            <div className="question-bank__upload-control">
              <label>Upload image for option {String.fromCharCode(64 + option.optionNumber)}
                <Input type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" disabled={uploadingOptionNumber !== null} onChange={(event) => handleOptionMediaChange(option.optionNumber, event)} />
              </label>
              {uploadingOptionNumber === option.optionNumber && <span><Spinner size="sm" /> Uploading media…</span>}
            </div>
            {isMediaOption && <span className="question-bank__media-link-label">Option {String.fromCharCode(64 + option.optionNumber)} media link <small>(read-only)</small></span>}
            {isMediaOption && (
              renderMediaLinkControls(mediaUrl, `Option ${String.fromCharCode(64 + option.optionNumber)} media`)
            )}
            {isMediaOption && renderMediaPreview(mediaUrl, `Option ${String.fromCharCode(64 + option.optionNumber)} media`)}
            {isMediaOption && (
              <Button
                type="button"
                color="light"
                className="question-bank__media-clear"
                aria-label={`Delete option ${option.optionNumber} media`}
                disabled={uploadingOptionNumber !== null || saving}
                onClick={() => setOption(option.optionNumber, { value: '', optionFile: undefined, optionType: 'text' })}
              >
                <FiTrash2 /> Delete Media
              </Button>
            )}
          </div>
          );
        })}
      </div>

      <label className="question-bank__field">
        <span>Answer explanation <small>(optional)</small></span>
        <Input type="textarea" rows={3} value={draft.answerExplanation} onChange={(event) => setDraft({ ...draft, answerExplanation: event.target.value })} />
      </label>

      <div className="question-bank__editor-actions">
        {editingQuestion && (
          <Button type="button" color="danger" outline disabled={deleting || saving || questionMediaUploading || uploadingOptionNumber !== null} onClick={openDeleteDialog}>
            <FiTrash2 /> Delete from all lessons
          </Button>
        )}
        <div className="question-bank__editor-actions-right">
          <Button type="button" color="light" disabled={saving || deleting || questionMediaUploading || uploadingOptionNumber !== null} onClick={closeEditor}>Cancel</Button>
          <Button type="submit" color="primary" disabled={saving || deleting || questionMediaUploading || uploadingOptionNumber !== null || assignmentRows.some((row) => Boolean(loadingAssignmentLevels[row.levelId]))}>
            {saving ? <><Spinner size="sm" /> Saving…</> : editingQuestion ? 'Save changes' : 'Add question'}
          </Button>
        </div>
      </div>
    </form>
  );

  return (
    <section className="question-bank">
      <div className="question-bank__page-heading">
        <div>
          <h2>FA/SA MCQ Editor</h2>
          <p>Manage questions in the quiz question bank.</p>
        </div>
        {!editingQuestion && !isAdding && <Button color="primary" onClick={beginAdd}><FiPlus /> Add question</Button>}
      </div>
      {!editingQuestion && !isAdding && renderFilters()}

      {editingQuestion || isAdding ? renderEditor() : (
        <div className="question-bank__list-area">
          {!filtersReady ? (
            <div className="question-bank__empty">Select a course, level, and lesson to view questions.</div>
          ) : loadingQuestions ? (
            <div className="question-bank__loading"><Spinner /> Loading questions…</div>
          ) : questions.length === 0 ? (
            <div className="question-bank__empty">No questions found for this lesson. Add the first question.</div>
          ) : (
            <>
              <div className="question-bank__list-heading"><strong>{questions.length} question{questions.length === 1 ? '' : 's'}</strong><span>Select a question to view or edit it.</span></div>
              <div className="question-bank__cards">
                {questions.map((question, index) => {
                  const correctAnswers = question.answer ?? [];
                  const hasRepeatedCorrectText = question.answerType === 'single' && correctAnswers.some((answer) =>
                    (question.options ?? []).filter((option) => option.value === answer && !mediaTypeFromUrl(option.value)).length > 1
                  );
                  return (
                  <button type="button" className={`question-bank__card${hasRepeatedCorrectText ? ' has-duplicate-correct-answer' : ''}`} key={question.id} onClick={() => beginEdit(question)}>
                    <div className="question-bank__card-top">
                      <span>Question {index + 1}</span>
                      <span>{question.answerType === 'multiple' ? 'Multiple answers' : 'Single answer'}</span>
                    </div>
                    <div className="question-bank__card-question">{question.question}</div>
                    <ul className="question-bank__option-preview">
                      {(question.options ?? []).slice().sort((left, right) => left.optionNumber - right.optionNumber).map((option) => {
                        const isCorrect = (question.answer ?? []).includes(option.value);
                        return (
                          <li key={option.optionNumber} className={isCorrect ? 'is-correct' : ''}>
                            <span>{String.fromCharCode(64 + option.optionNumber)}.</span>
                            <span>{option.value}</span>
                            {isCorrect && <b>Answer</b>}
                          </li>
                        );
                      })}
                    </ul>
                    <div className="question-bank__card-meta">
                      <span>{formatQuestionType(question.questionType)} · {question.options?.length ?? 0} options</span>
                    </div>
                    <div className="question-bank__card-locations">
                      <strong>FA/SA lessons</strong>
                      {(question.locations?.length ? question.locations : [{
                        id: question.id,
                        courseName: question.courseName,
                        courseLevel: question.courseLevel,
                        lessonNumber: question.lessonNumber,
                      }]).map((location) => (
                        <span key={location.id}>
                          {location.courseName || 'Course'} · {location.courseLevel || 'Level'} · Lesson {location.lessonNumber}
                        </span>
                      ))}
                    </div>
                    <FiEdit2 className="question-bank__card-edit" />
                  </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {isDeleteDialogOpen && editingQuestion && (
        <div className="question-bank__preview-backdrop">
          <section className="question-bank__delete-dialog" role="alertdialog" aria-modal="true" aria-labelledby="question-bank-delete-title">
            <div className="question-bank__preview-header">
              <div>
                <span className="question-bank__preview-eyebrow">Question bank</span>
                <h3 id="question-bank-delete-title">Delete question from all lessons?</h3>
              </div>
              <Button type="button" color="link" className="question-bank__preview-close" aria-label="Close delete dialog" disabled={deleting} onClick={() => setIsDeleteDialogOpen(false)}>
                <FiX />
              </Button>
            </div>
            <div className="question-bank__delete-content">
              <p>This will delete the question from every linked FA/SA lesson:</p>
              <ul className="question-bank__delete-locations">
                {(editingQuestion.locations?.length ? editingQuestion.locations : [{
                  id: editingQuestion.id,
                  courseId: editingQuestion.courseId,
                  courseName: editingQuestion.courseName,
                  levelId: editingQuestion.levelId,
                  courseLevel: editingQuestion.courseLevel,
                  lessonNumber: editingQuestion.lessonNumber,
                }]).map((location) => (
                  <li key={location.id}>{labelForQuestionLocation(location)}</li>
                ))}
              </ul>
              {checkingDeleteEligibility && (
                <div className="question-bank__delete-status"><Spinner size="sm" /> Checking minimum question counts…</div>
              )}
              {deleteCheckError && <p className="question-bank__delete-error" role="alert">{deleteCheckError} Delete is unavailable until the lesson counts can be checked.</p>}
              {!checkingDeleteEligibility && !deleteCheckError && deleteBlockedLessons.length > 0 && (
                <div className="question-bank__delete-blocked" role="alert">
                  <strong>This question can’t be deleted from all lessons.</strong>
                  <p>Deleting it would take these lessons below their minimums. Remove the question from eligible lessons individually instead.</p>
                  <ul>
                    {deleteBlockedLessons.map((blocked) => <li key={blocked.id}>{blocked.label}: {blocked.reason}</li>)}
                  </ul>
                </div>
              )}
              {!checkingDeleteEligibility && !deleteCheckError && deleteBlockedLessons.length === 0 && (
                <p className="question-bank__delete-allowed">The minimum question count will be preserved in every linked lesson.</p>
              )}
              <div className="question-bank__delete-actions">
                <Button type="button" color="light" disabled={deleting} onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
                <Button type="button" color="danger" disabled={deleting || checkingDeleteEligibility || Boolean(deleteCheckError) || deleteBlockedLessons.length > 0} onClick={confirmDeleteAll}>
                  {deleting && !checkingDeleteEligibility ? <><Spinner size="sm" /> Deleting…</> : 'Delete from all lessons'}
                </Button>
              </div>
            </div>
          </section>
        </div>
      )}

      {isPreviewOpen && (
        <div className="question-bank__preview-backdrop" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setIsPreviewOpen(false);
        }}>
          <section className="question-bank__preview-dialog" role="dialog" aria-modal="true" aria-labelledby="question-bank-preview-title">
            <div className="question-bank__preview-header">
              <div>
                <span className="question-bank__preview-eyebrow">Learner preview</span>
                <h3 id="question-bank-preview-title">Question preview</h3>
              </div>
              <Button type="button" color="link" className="question-bank__preview-close" aria-label="Close preview" onClick={() => setIsPreviewOpen(false)}>
                <FiX />
              </Button>
            </div>
            <div className="question-bank__preview-content">
              <div className="question-bank__preview-number">Question {editingQuestion ? Math.max(1, questions.findIndex((question) => question.id === editingQuestion.id) + 1) : questions.length + 1}</div>
              <article className="question-bank__learner-card">
                {renderMediaPreview(draft.questionFile, 'Question media')}
                <h4>{draft.question || 'Your question will appear here.'}</h4>
                <div className="question-bank__learner-options">
                  {draft.options.map((option) => {
                    const selected = previewSelection.includes(option.optionNumber);
                    const isCorrect = draft.correctOptionNumbers.includes(option.optionNumber);
                    const optionMediaUrl = option.optionFile || (mediaTypeFromUrl(option.value) ? option.value : '');
                    const optionMediaType = mediaTypeFromUrl(optionMediaUrl);
                    const resultClass = previewSubmitted
                      ? isCorrect ? 'is-correct' : selected ? 'is-wrong' : ''
                      : selected ? 'is-selected' : '';
                    return (
                      <button
                        type="button"
                        key={option.optionNumber}
                        className={`question-bank__learner-option ${resultClass}`}
                        aria-pressed={selected}
                        onClick={() => togglePreviewOption(option.optionNumber)}
                        disabled={previewSubmitted}
                      >
                        <span className="question-bank__learner-option-content">
                          {optionMediaUrl && optionMediaType === 'image' && <img src={optionMediaUrl} alt={`Option ${option.optionNumber}`} />}
                          {optionMediaUrl && optionMediaType === 'video' && <video src={optionMediaUrl} controls preload="metadata" aria-label={`Option ${option.optionNumber}`} />}
                          {optionMediaUrl && optionMediaType === 'audio' && <audio src={optionMediaUrl} controls preload="metadata" aria-label={`Option ${option.optionNumber}`} />}
                          {!optionMediaUrl && (option.value || `Option ${option.optionNumber}`)}
                        </span>
                        {previewSubmitted && isCorrect && <span className="question-bank__learner-option-result">Correct</span>}
                      </button>
                    );
                  })}
                </div>
                {previewSubmitted && (
                  <div className={`question-bank__preview-feedback ${previewIsCorrect ? 'is-correct' : 'is-wrong'}`} role="status">
                    {previewIsCorrect ? '👏 Correct Answer' : '❗ Wrong Answer'}
                  </div>
                )}
                <div className="question-bank__preview-actions">
                  {previewSubmitted ? (
                    <Button type="button" color="primary" onClick={openPreview}>Try again</Button>
                  ) : (
                    <Button type="button" color="primary" disabled={previewSelection.length === 0} onClick={() => setPreviewSubmitted(true)}>Submit answer</Button>
                  )}
                </div>
              </article>
            </div>
          </section>
        </div>
      )}
    </section>
  );
};

export default QuestionBankEditor;
